import type { TEvent } from "../types";
import type { TrackingAdapter } from "../events/subscriber";
import { createAdapterLogger, type Logger, type LoggerConfig } from "../logger";

/**
 * Abstract base class for tracking adapters
 */
export abstract class BaseAdapter implements TrackingAdapter {
  /**
   * Name of the tracking adapter
   */
  public abstract readonly name: string;

  /**
   * Configuration for the adapter
   */
  protected config: Record<string, any>;

  /**
   * Logger instance for this adapter
   */
  protected logger: Logger;

  /**
   * Whether the adapter has been initialized
   */
  protected initialized = false;

  /**
   * Constructor
   * @param config Configuration for the adapter
   */
  constructor(config: Record<string, any> = {}) {
    this.config = config;

    // Create a generic logger - will be properly initialized by subclasses
    const loggerConfig: LoggerConfig = {
      enabled: config.enableDebugLogs ?? true,
      level: config.logLevel ?? "info",
      name: "@shopkit/analytics",
    };

    this.logger = createAdapterLogger("BaseAdapter", loggerConfig);
  }

  /**
   * Initialize logger with proper adapter name
   * Should be called by subclasses after name is available
   */
  protected initializeLogger(): void {
    const loggerConfig: LoggerConfig = {
      enabled: this.config.enableDebugLogs ?? true,
      level: this.config.logLevel ?? "info",
      name: "@shopkit/analytics",
    };

    this.logger = createAdapterLogger(this.name, loggerConfig);
  }

  /**
   * Check if the adapter is enabled
   */
  public isEnabled(): boolean {
    return this.initialized;
  }

  /**
   * Initialize the adapter
   */
  public abstract initialize(): Promise<void>;

  /**
   * Track an event
   * @param event The event to track
   */
  public abstract trackEvent(event: TEvent): Promise<void>;

  /**
   * Get a configuration value
   * @param key The configuration key
   * @param defaultValue The default value if the key is not found
   */
  protected getConfig<T>(key: string, defaultValue?: T): T {
    return key in this.config ? this.config[key] : (defaultValue as T);
  }

  /**
   * Get affiliate parameters from session storage or custom implementation
   * Override this method to provide your own affiliate tracking implementation
   * @returns Affiliate parameters or null if not available
   */
  protected getAffiliateParams(): Record<string, string> | null {
    // Default implementation returns null
    // Users can override this method to integrate with their affiliate tracking system
    return null;
  }

  /**
   * Enhance event parameters with affiliate data
   * @param params The original parameters
   * @returns Parameters enhanced with affiliate data
   */
  protected enhanceWithAffiliateParams(
    params: Record<string, any>
  ): Record<string, any> {
    const affiliateParams = this.getAffiliateParams();
    if (!affiliateParams) return params;

    return {
      ...params,
      // Add affiliate parameters
      ...affiliateParams,
    };
  }
}
